import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { motion } from "framer-motion";
// Import components
import RefreshButton from "../components/admin/RefreshButton";
import StatsSummary from "../components/admin/StatsSummary";
import CombinedTrendChart from "../components/admin/CombinedTrendChart";
import StatusChart from "../components/admin/StatusChart";
import StatusTable from "../components/admin/StatusTable";
import { SECONDARY_API_URL } from "../config/api";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  ArcElement,
  TimeScale,
  Title,
  BarElement,
} from "chart.js";

// Register the required Chart.js components
ChartJS.register(
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  ArcElement,
  TimeScale,
  Title,
  BarElement
);

// Status color mapping for charts with white for pending
const chartStatusColors = {
  pending: "#ffffff", // white
  confirmed: "#22c55e", // green
  completed: "#3b82f6", // blue
  cancelled: "#ef4444", // red
};

// Animation variants for staggered animation
const fadeInVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: index => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.1 * index,
      duration: 0.5,
      ease: "easeOut"
    }
  })
};

// Analytics Component 
const Analytics = () => {
  // Use the global chartStatusColors from above instead of redefining it
  const chartStatusColorsForAnalytics = chartStatusColors;
  
  const [bookings, setBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [timeUnit, setTimeUnit] = useState("day");
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  
  // Chart data state
  const [totalEarnings, setTotalEarnings] = useState(0);
  const [averageBookingPrice, setAverageBookingPrice] = useState(0);
  const [routeDistribution, setRouteDistribution] = useState([]);
  const [statusData, setStatusData] = useState({ labels: [], data: [], colors: [] });
  const [monthlyData, setMonthlyData] = useState([]);
  const [conversionRate, setConversionRate] = useState(0);
  
  // Ref to store processed data for each time unit to prevent repeated processing
  const processedDataCache = useRef({});
  
  // Helper function to get ISO week number
  const getWeekNumber = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  };
  
  // Format time data according to timeUnit
  const formatTimeLabel = useCallback((item) => {
    const date = new Date(item.date);
    switch (timeUnit) {
      case 'day': return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
      case 'week': return `Week ${item.week}`;
      case 'month': return date.toLocaleDateString('en-US', { month: 'short' });
      case 'quarter': return `Q${item.quarter}`;
      case 'year': return item.year.toString();
      default: return date.toLocaleDateString('en-US', { month: 'short' });
    }
  }, [timeUnit]);
  
  // Create memoized chart data objects from monthlyData
  // This reduces the number of state variables and dependencies
  const { statusChartData, combinedChartData } = useMemo(() => {
    // Ensure status colors are properly formatted as strings
    const safeColors = statusData.colors.map(color => 
      typeof color === 'string' ? color : '#999999'
    );
    
    // Status chart data
    const statusChart = {
      labels: statusData.labels,
      datasets: [{
        data: statusData.data,
        backgroundColor: safeColors,
        borderWidth: 1
      }]
    };
    
    // If monthlyData is empty, return empty data
    if (!monthlyData.length) {
      return { 
        statusChartData: statusChart, 
        combinedChartData: {
          labels: [],
          datasets: [
            { label: 'Earnings', data: [], borderColor: '#ffffff', backgroundColor: 'rgba(255, 255, 255, 0.1)', yAxisID: 'y-earnings' },
            { label: 'Bookings', data: [], borderColor: '#3b82f6', backgroundColor: 'rgba(59, 130, 246, 0.1)', yAxisID: 'y-bookings' }
          ]
        }
      };
    }
    
    // Format labels for time-based charts
    const labels = monthlyData.map(formatTimeLabel);
    
    // Combined chart data
    const combinedChart = {
      labels,
      datasets: [
        {
          label: 'Earnings',
          data: monthlyData.map(item => item.earnings),
          borderColor: '#ffffff',
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          yAxisID: 'y-earnings'
        },
        {
          label: 'Bookings',
          data: monthlyData.map(item => item.count),
          borderColor: '#3b82f6',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          yAxisID: 'y-bookings'
        }
      ]
    };
    
    return { statusChartData: statusChart, combinedChartData: combinedChart };
  }, [monthlyData, statusData, formatTimeLabel]);

  // Chart options with better transitions and animations
  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: { position: 'bottom' },
      tooltip: { enabled: true }
    },
    animation: {
      duration: 800,
      easing: 'easeOutQuart'
    },
    transitions: {
      show: {
        animations: {
          x: { from: 0 },
          y: { from: 0 }
        }
      }
    }
  }), []);

  const combinedChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: { display: true, position: 'top' },
      tooltip: { enabled: true }
    },
    animation: {
      duration: 800,
      easing: 'easeOutQuart'
    },
    transitions: {
      show: {
        animations: {
          x: { from: 0 },
          y: { from: 0 }
        }
      }
    },
    scales: {
      'y-earnings': {
        position: 'left',
        beginAtZero: true,
        grid: { display: false },
        title: { display: true, text: 'Earnings (€)' }
      },
      'y-bookings': {
        position: 'right',
        beginAtZero: true,
        grid: { display: false },
        title: { display: true, text: 'Bookings' }
      },
      x: { grid: { display: false } }
    }
  }), []);

  // Fetch booking data
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    // Reset the processed data cache
    processedDataCache.current = {};
    
    try {
      const response = await axios.get(`${SECONDARY_API_URL}/api/bookings`, {
        params: { t: Date.now() },
        timeout: 8000
      });
      
      if (response.data && Array.isArray(response.data)) {
        console.log(`Fetched ${response.data.length} bookings`);
        setBookings(response.data);
      } else {
        console.error("Invalid response format:", response.data);
        toast.error("Invalid data format received from server");
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error fetching bookings for analytics:", error);
      toast.error("Failed to load analytics data. Please try again.");
      setIsLoading(false);
    }
  }, []);
  
  // Initial fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  // Process bookings data when bookings or timeUnit changes
  useEffect(() => {
    if (!bookings.length) return;
    
    // Check if we already have cached results for this time unit
    if (processedDataCache.current[timeUnit]) {
      const cachedResults = processedDataCache.current[timeUnit];
      setTotalEarnings(cachedResults.totalEarnings);
      setAverageBookingPrice(cachedResults.averageBookingPrice);
      setRouteDistribution(cachedResults.routeDistribution);
      setMonthlyData(cachedResults.monthlyData);
      setStatusData(cachedResults.statusData);
      setConversionRate(cachedResults.conversionRate);
      setIsLoading(false);
      setIsProcessing(false);
      return;
    }
    
    // Using a setTimeout to ensure there's a visual indicator of transition
    // This helps prevent flickering by ensuring loading state is visible
    setIsProcessing(true);
    const processingTimer = setTimeout(() => {
      try {
        // Group by time unit
        const groupByTimeUnit = (booking) => {
          const date = new Date(booking.datetime);
          switch (timeUnit) {
            case 'day': return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
            case 'week': return `${date.getFullYear()}-W${getWeekNumber(date).toString().padStart(2, '0')}`;
            case 'month': return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
            case 'quarter': return `${date.getFullYear()}-Q${Math.floor(date.getMonth() / 3) + 1}`;
            case 'year': return `${date.getFullYear()}`;
            default: return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
          }
        };
        
        // Process all data in one pass
        const results = bookings.reduce((acc, booking) => {
          const timePeriod = groupByTimeUnit(booking);
          
          // Accumulate total price
          acc.totalPrice += booking.price;
          
          // Process route distribution
          const routeKey = `${booking.pickup_name} to ${booking.dropoff_name}`;
          if (!acc.routes[routeKey]) {
            acc.routes[routeKey] = { 
              count: 0, totalPrice: 0,
              pickupName: booking.pickup_name,
              dropoffName: booking.dropoff_name
            };
          }
          acc.routes[routeKey].count++;
          acc.routes[routeKey].totalPrice += booking.price;
          
          // Process status counts
          if (acc.statuses[booking.status] !== undefined) {
            acc.statuses[booking.status]++;
          }
          
          // Process time period data
          if (!acc.timePeriods[timePeriod]) {
            const date = new Date(booking.datetime);
            
            let timeInfo = {
              count: 0,
              earnings: 0,
              date: new Date(date.getFullYear(), date.getMonth(), 1)
            };
            
            switch (timeUnit) {
              case 'day':
                timeInfo.date = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                timeInfo.day = date.getDate();
                timeInfo.month = date.getMonth();
                timeInfo.year = date.getFullYear();
                break;
              case 'week':
                const weekNum = getWeekNumber(date);
                const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
                const daysOffset = (weekNum - 1) * 7;
                timeInfo.date = new Date(firstDayOfYear.getTime() + daysOffset * 24 * 60 * 60 * 1000);
                timeInfo.week = weekNum;
                timeInfo.year = date.getFullYear();
                break;
              case 'month':
                timeInfo.date = new Date(date.getFullYear(), date.getMonth(), 1);
                timeInfo.month = date.getMonth();
                timeInfo.year = date.getFullYear();
                break;
              case 'quarter':
                const quarter = Math.floor(date.getMonth() / 3);
                timeInfo.date = new Date(date.getFullYear(), quarter * 3, 1);
                timeInfo.quarter = quarter + 1;
                timeInfo.year = date.getFullYear();
                break;
              case 'year':
                timeInfo.date = new Date(date.getFullYear(), 0, 1);
                timeInfo.year = date.getFullYear();
                break;
            }
            
            acc.timePeriods[timePeriod] = timeInfo;
          }
          
          acc.timePeriods[timePeriod].count++;
          acc.timePeriods[timePeriod].earnings += booking.price;
          
          return acc;
        }, {
          totalPrice: 0,
          routes: {},
          statuses: { pending: 0, confirmed: 0, completed: 0, cancelled: 0 },
          timePeriods: {}
        });
        
        // Calculate derived data
        const avgPrice = bookings.length > 0 ? results.totalPrice / bookings.length : 0;
        const routeDistribution = Object.keys(results.routes)
          .map(key => ({
            route: key,
            count: results.routes[key].count,
            totalPrice: results.routes[key].totalPrice,
            pickupName: results.routes[key].pickupName,
            dropoffName: results.routes[key].dropoffName
          }))
          .sort((a, b) => b.count - a.count);
        const conversionRate = bookings.length > 0 
          ? (results.statuses.completed / bookings.length) * 100 
          : 0;
        const timePeriodsData = Object.values(results.timePeriods)
          .sort((a, b) => a.date - b.date);
        
        // Ensure all status keys exist in chartStatusColorsForAnalytics
        const statusKeys = Object.keys(results.statuses);
        const statusValues = Object.values(results.statuses);
        
        // Create correctly formatted color array where each element is a valid string
        const statusColors = statusKeys.map(status => {
          // Ensure the color exists in our mapping, or use a fallback color
          return chartStatusColorsForAnalytics[status] || "#999999";
        });
        
        // Create status data with properly formatted values
        const formattedStatusData = {
          labels: statusKeys.map(status => status.charAt(0).toUpperCase() + status.slice(1)),
          data: statusValues,
          colors: statusColors // Ensure all colors are valid strings
        };
        
        // Cache the processed results
        processedDataCache.current[timeUnit] = {
          totalEarnings: results.totalPrice,
          averageBookingPrice: avgPrice,
          routeDistribution,
          monthlyData: timePeriodsData,
          statusData: formattedStatusData,
          conversionRate
        };
        
        // Update state with processed data
        setTotalEarnings(results.totalPrice);
        setAverageBookingPrice(avgPrice);
        setRouteDistribution(routeDistribution);
        setMonthlyData(timePeriodsData);
        setStatusData(formattedStatusData);
        setConversionRate(conversionRate);
        
        // Mark first load as complete
        setIsFirstLoad(false);
      } catch (error) {
        console.error("Error processing data:", error);
        toast.error("An error occurred while processing the data");
      } finally {
        setIsLoading(false);
        setIsProcessing(false);
      }
    }, 300); // 300ms delay for loading state visibility
    
    return () => clearTimeout(processingTimer);
  }, [bookings, timeUnit, getWeekNumber, chartStatusColorsForAnalytics]);
  
  const formatPrice = (value) => new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "EUR"
  }).format(value);

  // Time unit change handler with debouncing
  const handleTimeUnitChange = useCallback((unit) => {
    if (unit === timeUnit || isLoading || isProcessing) return;
    
    // Set processing immediately to prevent multiple clicks
    setIsProcessing(true);
    setTimeUnit(unit);
  }, [timeUnit, isLoading, isProcessing]);

  // Combined loading state for UI
  const isDataLoading = isLoading || isProcessing;

  return (
    <div className="mx-auto max-w-7xl">
      <motion.div 
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="flex flex-wrap justify-between items-center mb-6"
      >
        <div>
          <h2 className="text-xl font-bold text-white">Analytics & Insights</h2>
          <p className="text-gray-400 text-sm">Track your business performance</p>
        </div>
        
        <div className="flex gap-4 items-center">
          <RefreshButton 
            onClick={fetchData} 
            isLoading={isDataLoading}
            text="Refresh Analytics"
          />
          
          <div className="flex rounded-lg overflow-hidden bg-gray-900 border border-gray-700">
            {['day', 'week', 'month', 'quarter', 'year'].map((unit) => (
              <button
                key={unit}
                disabled={isDataLoading}
                className={`px-3 py-1.5 text-sm font-medium ${
                  timeUnit === unit
                    ? 'bg-white/30 text-white'
                    : 'text-gray-400 hover:bg-gray-800'
                } ${isDataLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={() => handleTimeUnitChange(unit)}
              >
                {unit.charAt(0).toUpperCase() + unit.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Stats Summary with animation */}
      <motion.div
        initial="hidden"
        animate="visible"
        custom={0}
        variants={fadeInVariants}
      >
        <StatsSummary 
          isLoading={isDataLoading}
          totalEarnings={totalEarnings}
          averageBookingPrice={averageBookingPrice}
          conversionRate={conversionRate}
          bookings={bookings}
          formatPrice={formatPrice}
        />
      </motion.div>
      
      {/* Charts Grid Layout with staggered animation */}
      <motion.div 
        className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3, delay: 0.1 }}
      >
        {/* Combined Chart */}
        <motion.div
          initial="hidden"
          animate="visible"
          custom={1}
          variants={fadeInVariants}
          className="flex items-center justify-center"
        >
          <CombinedTrendChart 
            isLoading={isDataLoading}
            combinedChartData={combinedChartData}
            combinedChartOptions={combinedChartOptions}
          />
        </motion.div>
        
        {/* Status Distribution */}
        <motion.div
          initial="hidden"
          animate="visible"
          custom={2}
          variants={fadeInVariants}
          className="flex items-center justify-center"
        >
          <StatusChart 
            isLoading={isDataLoading}
            statusChartData={statusChartData}
            chartOptions={chartOptions}
          />
        </motion.div>
      </motion.div>
      
      {/* Status Table with animation */}
      <motion.div
        initial="hidden"
        animate="visible"
        custom={3}
        variants={fadeInVariants}
      >
        <StatusTable 
          isLoading={isDataLoading}
          statusData={statusData}
        />
      </motion.div>
    </div>
  );
};

export default Analytics; 